/**
 * AI Usage & Logs Page
 *
 * Admin page for monitoring AI request logs, token consumption,
 * and cost tracking with filtering and pagination.
 * Uses Card/Paper patterns matching TaskManagement/BookingManagement conventions.
 */

import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Grid,
  Card,
  Paper,
  Button,
  Table,
  Badge,
  Select,
  NumberInput,
  Loader,
  Alert,
  ThemeIcon,
  Divider,
} from '@/components/ui';
import { ActionIcon } from '@mantine/core';
import {
  IconRefresh,
  IconAlertCircle,
  IconArrowLeft,
  IconChevronLeft,
  IconChevronRight,
  IconCoins,
  IconClock,
  IconHash,
  IconActivity,
  IconChartBar,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAiRequestLogService } from '@actschurch/ccms-sdk';
import type { AiRequestLog } from '@actschurch/ccms-sdk';

const PAGE_SIZE_OPTIONS = [
  { value: '10', label: '10 per page' },
  { value: '25', label: '25 per page' },
  { value: '50', label: '50 per page' },
  { value: '100', label: '100 per page' },
];

const FEATURE_TYPE_FILTER = [
  { value: '', label: 'All Features' },
  { value: 'chat', label: 'Chat' },
  { value: 'summarization', label: 'Summarization' },
  { value: 'data_extraction', label: 'Data Extraction' },
  { value: 'workflow', label: 'Workflow' },
  { value: 'classification', label: 'Classification' },
  { value: 'generation', label: 'Generation' },
];

export default function UsagePage() {
  const navigate = useNavigate();
  const service = useAiRequestLogService();

  const [vendorId, setVendorId] = useState<number>(1);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [featureFilter, setFeatureFilter] = useState<string>('');

  // Fetch logs
  const {
    data: logs = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['ai-request-logs', vendorId, page, pageSize],
    queryFn: () => service.getByVendor(vendorId, page, pageSize),
  });

  // Apply client-side feature filter
  const filteredLogs = featureFilter
    ? logs.filter((log: AiRequestLog) => log.featureType === featureFilter)
    : logs;

  // Compute summary stats from current page
  const stats = {
    totalRequests: filteredLogs.length,
    totalTokens: filteredLogs.reduce((sum: number, log: AiRequestLog) => sum + (log.totalTokens || 0), 0),
    totalCost: filteredLogs.reduce((sum: number, log: AiRequestLog) => sum + (log.estimatedCostCents || 0), 0),
    avgDuration: filteredLogs.length > 0
      ? Math.round(
          filteredLogs.reduce((sum: number, log: AiRequestLog) => sum + (log.durationMs || 0), 0) /
            filteredLogs.length
        )
      : 0,
  };

  const formatTimestamp = (ts: number) => {
    if (!ts) return '--';
    // CCMS timestamp format: yyyyMMddHHmmss (e.g. 20260417143403)
    const s = String(ts);
    if (s.length === 14) {
      return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)} ${s.slice(8, 10)}:${s.slice(10, 12)}:${s.slice(12, 14)}`;
    }
    return s;
  };

  const formatDuration = (ms: number | null | undefined) => {
    if (ms == null) return '--';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatCost = (cents: number | null | undefined) => {
    if (cents == null) return '--';
    return `$${(cents / 100).toFixed(4)}`;
  };

  const getStatusColor = (statusCode: number) => {
    if (statusCode >= 200 && statusCode < 300) return 'green';
    if (statusCode >= 400 && statusCode < 500) return 'orange';
    return 'red';
  };

  return (
    <Container fluid py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <ActionIcon variant="subtle" onClick={() => navigate('/admin/ai')}>
              <IconArrowLeft size={20} />
            </ActionIcon>
            <div>
              <Title order={2}>Usage & Logs</Title>
              <Text c="dimmed" size="sm">
                Monitor AI request logs, token consumption, and costs.
              </Text>
            </div>
          </Group>
        </Group>

        {/* KPI Stat Cards */}
        <Grid>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="blue">
                  <IconActivity size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Requests
                  </Text>
                  <Text size="xl" fw={700}>
                    {stats.totalRequests}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="violet">
                  <IconHash size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Total Tokens
                  </Text>
                  <Text size="xl" fw={700}>
                    {stats.totalTokens.toLocaleString()}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="yellow">
                  <IconCoins size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Est. Cost
                  </Text>
                  <Text size="xl" fw={700}>
                    {formatCost(stats.totalCost)}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="teal">
                  <IconClock size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Avg Duration
                  </Text>
                  <Text size="xl" fw={700}>
                    {formatDuration(stats.avgDuration)}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Filter Bar in Paper */}
        <Paper shadow="xs" p="md" radius="md" withBorder>
          <Group>
            <NumberInput
              label="Vendor ID"
              value={vendorId}
              onChange={(val) => {
                setVendorId(Number(val) || 1);
                setPage(1);
              }}
              min={1}
              w={120}
            />
            <Select
              label="Feature Type"
              data={FEATURE_TYPE_FILTER}
              value={featureFilter}
              onChange={(val) => setFeatureFilter(val || '')}
              w={180}
              clearable
            />
            <Select
              label="Page Size"
              data={PAGE_SIZE_OPTIONS}
              value={String(pageSize)}
              onChange={(val) => {
                setPageSize(Number(val) || 25);
                setPage(1);
              }}
              w={140}
              allowDeselect={false}
            />
          </Group>
        </Paper>

        {/* Error */}
        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Error" withCloseButton>
            {(error as Error).message || 'Failed to load logs'}
          </Alert>
        )}

        {/* Loading */}
        {isLoading && (
          <Group justify="center" p="xl">
            <Loader size="lg" />
          </Group>
        )}

        {/* Table in Card */}
        {!isLoading && (
          <Card shadow="sm" padding={0} radius="md" withBorder>
            {/* Card Header */}
            <Group justify="space-between" p="lg" pb="sm">
              <Group gap="xs">
                <IconChartBar size={20} />
                <Title order={4}>Request Logs</Title>
              </Group>
              <Button
                variant="subtle"
                size="sm"
                leftSection={<IconRefresh size={16} />}
                onClick={() => refetch()}
                loading={isLoading}
              >
                Refresh
              </Button>
            </Group>
            <Divider />

            <Table tableVariant="striped">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Timestamp</Table.Th>
                  <Table.Th>User</Table.Th>
                  <Table.Th>Feature</Table.Th>
                  <Table.Th>Model</Table.Th>
                  <Table.Th>Tokens</Table.Th>
                  <Table.Th>Duration</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Cost</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredLogs.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={8}>
                      <Text ta="center" c="dimmed" py="lg">
                        No request logs found for the selected filters.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {filteredLogs.map((log: AiRequestLog) => (
                  <Table.Tr key={log.requestLogID}>
                    <Table.Td>
                      <Text size="xs">{formatTimestamp(log.createdDateTime)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" truncate="end" maw={120}>
                        {log.userId || '--'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="blue" size="sm">
                        {log.featureType}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{log.modelId}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="xs" fw={500}>
                          {(log.totalTokens || 0).toLocaleString()}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {(log.inputTokens || 0).toLocaleString()} in / {(log.outputTokens || 0).toLocaleString()} out
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{formatDuration(log.durationMs)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={getStatusColor(log.statusCode)} variant="filled" size="sm">
                        {log.statusCode}
                      </Badge>
                      {log.errorMessage && (
                        <Text size="xs" c="red" lineClamp={1}>
                          {log.errorMessage}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" fw={500}>
                        {formatCost(log.estimatedCostCents)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {/* Pagination in Card Footer */}
            <Divider />
            <Group justify="center" p="md">
              <Button
                variant="default"
                size="sm"
                leftSection={<IconChevronLeft size={14} />}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Text size="sm" c="dimmed">
                Page {page}
              </Text>
              <Button
                variant="default"
                size="sm"
                rightSection={<IconChevronRight size={14} />}
                disabled={logs.length < pageSize}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </Group>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
